import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axiosInstance from "../../Utils/axiosInstance";
import { getNotificationSocket } from "./NotificationContext";
import { getToken } from "../../Utils/auth";

const WalletContext = createContext(null);

export const WalletProvider = ({ children }) => {
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const userIdRef = useRef(null);

  // ── Resolve userId ────────────────────────────────────────────────────────
  const getUserId = useCallback(() => {
    if (userIdRef.current) return userIdRef.current;
    try {
      const raw = localStorage.getItem("qqc_userData");
      if (!raw) return null;
      const user = JSON.parse(raw);
      const id = user?.id || user?._id;
      if (id) userIdRef.current = id;
      return id || null;
    } catch {
      return null;
    }
  }, []);

  // ── Fetch balance from API ────────────────────────────────────────────────
  const fetchWalletBalance = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      const { data } = await axiosInstance.get(
        `/wallet/get-wallet-balance/${userId}`,
      );
      if (data.success) {
        setWalletBalance(data.data.availableBalance);
      }
    } catch (err) {
      console.error("[WalletContext] fetch failed:", err.message);
    } finally {
      setWalletLoading(false);
    }
  }, [getUserId]);

  // ── Initial fetch ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchWalletBalance();
  }, [fetchWalletBalance]);

  useEffect(() => {
    const init = (token) => {
      // Reset state for new user (or logout)
      setWalletBalance(null);
      setWalletLoading(true);
      userIdRef.current = null;

      // Detach old socket listener
      const oldSocket = getNotificationSocket();
      if (oldSocket) oldSocket.off("wallet:updated");

      if (!token) {
        setWalletLoading(false);
        return;
      }

      // Fetch balance for the new user
      fetchWalletBalance();

      // Attach wallet socket listener — poll until socket is ready
      let interval = null;
      let attached = false;

      const tryAttach = () => {
        const socket = getNotificationSocket();
        if (socket && socket.connected && !attached) {
          attached = true;
          socket.on("wallet:updated", (data) => {
            console.log("[WalletContext] wallet:updated received:", data);
            if (data?.availableBalance !== undefined) {
              setWalletBalance(data.availableBalance);
            }
          });
          clearInterval(interval);
        }
      };

      interval = setInterval(tryAttach, 500);
      tryAttach();

      // Return a cleanup for this init call
      return () => {
        clearInterval(interval);
        const socket = getNotificationSocket();
        if (socket) socket.off("wallet:updated");
      };
    };

    let cleanup = init(getToken());

    const handleAuthChanged = (e) => {
      if (cleanup) cleanup();
      cleanup = init(e.detail.token);
    };

    window.addEventListener("auth:changed", handleAuthChanged);

    return () => {
      window.removeEventListener("auth:changed", handleAuthChanged);
      if (cleanup) cleanup();
    };
  }, [fetchWalletBalance]);

  return (
    <WalletContext.Provider
      value={{
        walletBalance,
        walletLoading,
        refreshWalletBalance: fetchWalletBalance,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWallet must be used inside WalletProvider");
  }
  return context;
};
